import { z } from "zod";
// The customer contract of the lifecycle push notifications (CHT-764, Phase 12.4; docs/engineering/lifecycle-notifications.md).
//
// Strict shapes: a key that is not listed fails the parse. The customer id is NEVER in a path, a query or a body: it comes from the
// verified session. The push token never appears here: the provider SDK on the device keeps it and links it to the external id that
// `PUT /customer/notification-devices` answers.
const version = z.number().int().min(1).max(2_147_483_647);
const minuteOfDay = z.number().int().min(0).max(1439);
/** An IANA zone name such as `Asia/Kolkata`. The server validates it against the runtime zone list. */
const timeZone = z.string().min(1).max(64);
// --- push settings -----------------------------------------------------------------------------------------------------
/**
 * The push settings of a customer. The global switch stops every push. The trip category stops reminders and weather heads-ups.
 * Quiet hours hold an optional push until they end, in `timeZone`. Service messages (a payment due, a question from the concierge,
 * a confirmation) ignore the category and the quiet hours; only the global switch stops them. The server enforces all of it.
 */
export const customerPushSettingsSchema = z.strictObject({
    pushEnabled: z.boolean(),
    tripUpdates: z.boolean(),
    quietHours: z
        .strictObject({ startMinute: minuteOfDay, endMinute: minuteOfDay })
        .refine((hours) => hours.startMinute !== hours.endMinute, "start and end must differ")
        .nullable(),
    timeZone: timeZone.nullable(),
});
/** `GET /customer/push-settings`. A customer who never saved reads the defaults at version 1. */
export const customerPushSettingsViewSchema = z.strictObject({
    settings: customerPushSettingsSchema,
    version,
});
/** `PUT /customer/push-settings`. The first save carries version 1. A stale version is 409 `push_settings.version_conflict`. */
export const customerPushSettingsBodySchema = z.strictObject({
    expectedVersion: version,
    settings: customerPushSettingsSchema,
});
// --- devices -----------------------------------------------------------------------------------------------------------
export const notificationPlatformValues = ["IOS", "ANDROID"];
export const notificationPermissionValues = ["GRANTED", "DENIED", "NOT_ASKED"];
/** The shape of an Expo push token: `ExponentPushToken[...]` or `ExpoPushToken[...]`. The same rule as `EXPO_PUSH_TOKEN_PATTERN` of the domain. */
const expoPushTokenSchema = z.string().regex(/^Expo(?:nent)?PushToken\[[A-Za-z0-9_-]{8,64}\]$/u);
/**
 * `PUT /customer/notification-devices`. The app calls it after the session is trusted (sign-in, a restored session, a permission
 * change, a new push token). `installationId` is a random id the app made once per install: it names the device, never the person.
 * `pushToken` is the Expo push token of this install (Expo push, owner decision of 2026-10-18). It is left out when there is none (the
 * person did not allow push, or the device cannot get one, like a simulator). The server keeps it in one table, and it never leaves the
 * server again: no answer, log, event or export holds it. A different token for the same install replaces the old one.
 */
export const notificationDeviceBodySchema = z.strictObject({
    installationId: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/u),
    platform: z.enum(notificationPlatformValues),
    permission: z.enum(notificationPermissionValues),
    pushToken: expoPushTokenSchema.optional(),
    /**
     * The random key of the app session that makes the call (CHT-764 review fix 2). A sign-out closes the key at the server, and a bind that
     * names a closed key is refused (409). Left out by an older app.
     */
    sessionKey: z
        .string()
        .regex(/^[A-Za-z0-9_-]{16,64}$/u)
        .optional(),
});
/** The answer: the opaque id of the push identity. It is not the customer id, and it holds no token. */
export const notificationDeviceViewSchema = z.strictObject({
    externalId: z.uuid(),
});
/** `POST /customer/notification-devices/unbind`. The body names the device (the install id); the person is the session's. */
export const notificationDeviceUnbindBodySchema = z.strictObject({
    installationId: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/u),
    /** The key of the app session that signs out: it closes even when no binding is open yet (review fix 2). */
    sessionKey: z
        .string()
        .regex(/^[A-Za-z0-9_-]{16,64}$/u)
        .optional(),
});
/**
 * The body of `unbind-all`: empty (an older app), or the install and the session key that sign out, together (review fix 2). The session
 * closes in the same transaction that ends the identity, also when the identity did not exist yet.
 */
export const notificationDevicesUnbindAllBodySchema = z
    .strictObject({
    installationId: z
        .string()
        .regex(/^[A-Za-z0-9_-]{16,64}$/u)
        .optional(),
    sessionKey: z
        .string()
        .regex(/^[A-Za-z0-9_-]{16,64}$/u)
        .optional(),
})
    .refine((body) => (body.installationId === undefined) === (body.sessionKey === undefined), {
    message: "installationId and sessionKey go together",
});
/** The answer. `unbound: false` for a device that is unknown, unbound already or another person's, with no difference between the three. */
export const notificationDeviceUnbindViewSchema = z.strictObject({ unbound: z.boolean() });
/**
 * `POST /customer/notification-devices/unbind-all`. A sign-out of every device calls it before the sessions end: the push identity of the
 * account ends for good and every device is unbound. `unbound` is the number of devices that were bound (0 for a repeated call).
 */
export const notificationDevicesUnbindAllViewSchema = z.strictObject({
    unbound: z.number().int().min(0).max(10_000),
});
/** The route table of this contract, for the docs and for a drift test. Nothing here names a customer. */
export const LIFECYCLE_NOTIFICATION_ROUTES = Object.freeze({
    pushSettings: { method: "GET", path: "/customer/push-settings", idempotencyKey: "none" },
    pushSettingsUpdate: {
        method: "PUT",
        path: "/customer/push-settings",
        idempotencyKey: "required",
    },
    deviceBind: { method: "PUT", path: "/customer/notification-devices", idempotencyKey: "none" },
    deviceUnbind: {
        method: "POST",
        path: "/customer/notification-devices/unbind",
        idempotencyKey: "none",
    },
    deviceUnbindAll: {
        method: "POST",
        path: "/customer/notification-devices/unbind-all",
        idempotencyKey: "none",
    },
});
